import { ISearchCardV6 } from '~/models/SearchCardV6';
import { CommunityCode } from '~/constants/communities';
import { ApiService } from '../ApiService';
import {
  SignInPayload,
  SettingPayload,
  LocationPayload,
  PushTokenPayload,
  AccountSettingsResponse,
  FeatureAllowanceResponse,
  ChangePasswordPayload,
} from './AccountTypes';
import { IMemberProfileEdit, IProfileOptions } from '~/models/MemberProfileEdit';
import { AccountStatus } from '~/models/AccountStatus';
import { PaywallFeature, getPaywallFeatureName } from '~/models/Paywall';

// ACCOUNT SERVICE

export class AccountService {
  static async signIn(signInRequest: SignInPayload): Promise<AccountStatus> {
    const response = await ApiService.post<AccountStatus>('account/signin', signInRequest);

    // Update the authToken if we received one (mobile platforms)
    // otherwise, it will be set in the cookies
    if (response.AuthToken) {
      ApiService.updateAuthToken(response.AuthToken);
    }

    return response;
  }

  static async signOut(): Promise<void> {
    return ApiService.post('account/signout', {});
  }

  static async checkIn(): Promise<AccountStatus> {
    return await ApiService.post<AccountStatus>('account/checkin', {});
  }

  static async getAccountSettings(): Promise<AccountSettingsResponse> {
    return ApiService.get<AccountSettingsResponse>('account/settings');
  }

  static async updateAccountSetting(payload: SettingPayload): Promise<void> {
    return ApiService.patch('account/settings', payload);
  }

  static async getMyTypeFilter(): Promise<ISearchCardV6> {
    return ApiService.get<ISearchCardV6>('account/filter');
  }

  static async getMyTypeFilterOptions(): Promise<ISearchCardV6> {
    return ApiService.get<ISearchCardV6>('account/filter/options');
  }

  static async getMyTypeFilterCount(card: ISearchCardV6): Promise<{ MatchingCount: number; TotalCount: number }> {
    return ApiService.post<{ MatchingCount: number; TotalCount: number }>('account/filter/count', card);
  }

  static async updateMyTypeFilter(card: ISearchCardV6): Promise<void> {
    return ApiService.post('account/filter', card);
  }

  static async enableMyTypeFilter(): Promise<void> {
    return ApiService.post('account/filter/enable', {});
  }

  static async disableMyTypeFilter(): Promise<void> {
    return ApiService.post('account/filter/disable', {});
  }

  /**
   * Gets the current user's profile data for editing
   */
  static async getProfile(): Promise<IMemberProfileEdit> {
    return ApiService.get<IMemberProfileEdit>('account/profile');
  }

  /**
   * Gets all available options for profile editing fields
   */
  static async getProfileOptions(): Promise<IProfileOptions> {
    return ApiService.get<IProfileOptions>('account/profile/options');
  }

  /**
   * Updates the current user's profile
   */
  static async updateProfile(profile: IMemberProfileEdit): Promise<IMemberProfileEdit> {
    return ApiService.put<IMemberProfileEdit>('account/profile', profile);
  }

  static async setLocation(
    data: LocationPayload,
    updatePlaceName: boolean
  ): Promise<LocationPayload | void> {
    // Backend expects body = LocationPayload and query param updatePlaceName
    return ApiService.request<LocationPayload | void>('POST', 'account/location', data, { updatePlaceName });
  }

  static async setPushToken(payload: PushTokenPayload): Promise<void> {
    return ApiService.post('account/push-token', payload);
  }

  /**
   * Removes this device's push token from a specific community so its server
   * stops sending pushes. Targets the given community with its stored auth
   * token, since the community being removed is not the active one.
   * @param community The community to unregister the device from
   * @param authToken The stored auth token for that community
   * @param token The device push token to remove
   */
  static async deletePushToken(
    community: CommunityCode,
    authToken: string,
    token: string
  ): Promise<void> {
    return ApiService.requestForCommunity('DELETE', community, authToken, 'account/push-token', {
      token,
    });
  }

  static async getFeatureAllowance(feature: PaywallFeature, logUsage: boolean = false): Promise<FeatureAllowanceResponse> {
    const featureName = getPaywallFeatureName(feature);
    const queryParams = logUsage ? `?logUsage=${logUsage}` : '';
    return ApiService.get<FeatureAllowanceResponse>(`account/feature/${featureName}/allowance${queryParams}`);
  }

  // ACCOUNT MANAGEMENT

  static async changePassword(payload: ChangePasswordPayload): Promise<{ AuthToken?: string }> {
    const response = await ApiService.post<{ AuthToken?: string }>('account/password/change', payload);
    if (response?.AuthToken) {
      ApiService.updateAuthToken(response.AuthToken);
    }
    return response;
  }

  static async changeUserName(newUserName: string): Promise<void> {
    return ApiService.post<void>('account/username', { NewUserName: newUserName });
  }

  static async changeEmail(newEmail: string): Promise<void> {
    return ApiService.post<void>('account/email/change', { NewEmail: newEmail });
  }

  static async deleteAccount(password: string): Promise<void> {
    return ApiService.delete<void>('account', undefined, {
      'X-Password-Confirmation': password,
    });
  }

  static async isUserNameAvailable(userName: string): Promise<void> {
    return ApiService.get<void>(`account/availability/username/${encodeURIComponent(userName)}`);
  }

  static async isEmailAvailable(email: string): Promise<void> {
    return ApiService.get<void>(`account/availability/email/${encodeURIComponent(email)}`);
  }

}
